/**
 * saleFinish — the pure half of "the server said the sale is saved, now what?"
 *
 * Born 2026-09-21: /sales-new had FOUR hand-copied finish routines. The normal
 * one kept being improved; the manager-override (insufficient stock) and the
 * reconnect-grace copies were forgotten, so those sales ended with a toast and
 * an empty cart — no Total / Tendered / CHANGE dialog, no print (471 prod sales
 * May–Sep 2026). The checklist now lives here ONCE; UnifiedSalesPage's
 * `finishOnlineSale` executes it for the two paths that were short.
 *
 * No React, no api calls — decisions only, so it can be unit-tested.
 */

// Authorization secrets ride on the request payload. They must never be merged
// into the object the success dialog keeps in state and hands to PrintEngine.
const SECRET_KEYS = [
  'manager_override_pin', 'jit_owner_pin', 'price_match_pin',
  'below_capital_pin', 'discount_pin',
];

export const OVERRIDE_NOTICE = 'Approved with manager override. Discrepancy ticket created.';

export function saleInvoiceNumber(resData) {
  return resData?.invoice_number || resData?.sale_number || '';
}

/** The object the success dialog shows + prints: request ⊕ response, secrets removed. */
export function buildSaleCompleteData(saleData, resData) {
  const merged = { ...(saleData || {}), ...(resData || {}) };
  for (const k of SECRET_KEYS) delete merged[k];
  if (merged.scpwd && typeof merged.scpwd === 'object') {
    const { pin, ...scpwd } = merged.scpwd;   // eslint-disable-line no-unused-vars
    merged.scpwd = scpwd;
  }
  merged.invoice_number = saleInvoiceNumber(resData);
  return merged;
}

/**
 * Which follow-up steps does this finished sale need?
 * Mirrors the normal path in UnifiedSalesPage.processSale one-for-one.
 */
export function saleFinishPlan(saleData, resData, { cropCreditConfig = null } = {}) {
  const balance = parseFloat(saleData?.balance) || 0;
  const hasCustomer = !!saleData?.customer_id;
  const owes = balance > 0 && hasCustomer;
  const paymentType = saleData?.payment_type;
  return {
    invoiceNumber: saleInvoiceNumber(resData),
    // official SI/CI did not auto-issue — cashier must bill it before the customer leaves
    birWarning: !!resData?.bir_auto_issue_failed,
    // fallback only: the pre-invoice flow already put the signature on the payload
    needsSignature: owes && !saleData?.signature,
    needsCropLink: owes && cropCreditConfig?.type === 'charged_to_crop',
    // digital / split: the receipt photo is mandatory
    needsDigitalReceipt: (paymentType === 'digital' || paymentType === 'split') && !!resData?.id,
  };
}
